import { API_CONFIG } from '../config/api';
import { getCurrentUserId } from './auth';
import { ApiValidationError } from '../types/auth';
import type {
  Customer,
  CreateCustomerData,
  UpdateCustomerData,
  CustomerResponse,
  CustomersListParams,
  CustomersListResult,
  CreateCustomerResponse,
  UpdateCustomerResponse,
  GetCustomerResponse,
  MergeCustomersResponse,
  CustomerOpenInvoices,
  CustomerOpenInvoicesResponse,
  RecordBulkCustomerPaymentData,
  BulkCustomerPaymentResult,
  BulkCustomerPaymentResponse,
  CustomerPaymentMethod,
} from '../types/customer';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getCustomers = async (params?: CustomersListParams): Promise<CustomersListResult> => {
  const queryParams = new URLSearchParams();

  if (params?.search) {
    queryParams.set('search', params.search);
  }
  if (params?.customer_type) {
    queryParams.set('customer_type', params.customer_type);
  }
  if (params?.active != null) {
    queryParams.set('active', params.active ? 'true' : 'false');
  }
  if (params?.has_balance != null) {
    queryParams.set('has_balance', params.has_balance ? 'true' : 'false');
  }
  if (params?.has_credit != null) {
    queryParams.set('has_credit', params.has_credit ? 'true' : 'false');
  }
  if (params?.sort) {
    queryParams.set('sort', params.sort);
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.GET_ALL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch customers');
  }

  const result: CustomerResponse = await response.json();
  const meta = result.meta ?? {
    current_page: 1,
    last_page: 1,
    per_page: result.data?.length ?? 0,
    total: result.data?.length ?? 0,
  };

  return {
    customers: result.data ?? [],
    meta,
  };
};

/** Fetches every page (for dropdowns and pickers that need the full customer list). */
export const getAllCustomers = async (): Promise<Customer[]> => {
  const all: Customer[] = [];
  let page = 1;
  let lastPage = 1;

  do {
    const result = await getCustomers({ per_page: 100, page });
    all.push(...result.customers);
    lastPage = result.meta.last_page;
    page += 1;
  } while (page <= lastPage);

  return all;
};

export const getCustomerById = async (customerId: number): Promise<Customer> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.GET_ALL}/${customerId}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch customer');
  }

  const result: GetCustomerResponse = await response.json();
  return result.data;
};

export const getCustomerOpenInvoices = async (customerId: number): Promise<CustomerOpenInvoices> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.OPEN_INVOICES}/${customerId}/open-invoices`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Failed to fetch open invoices:', response.status, errorText);

    let errorMessage = 'Failed to fetch open invoices';
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const result: CustomerOpenInvoicesResponse = await response.json();
  return result.data;
};

export const recordBulkCustomerPayment = async (
  customerId: number,
  paymentMethod: CustomerPaymentMethod,
  allocations: { sale_id: number; amount: number }[],
  options?: { notes?: string; paymentDate?: string; referenceNo?: string }
): Promise<BulkCustomerPaymentResult> => {
  const amount = allocations.reduce((sum, row) => sum + row.amount, 0);

  const payload: RecordBulkCustomerPaymentData = {
    amount,
    payment_method: paymentMethod,
    reference_no: options?.referenceNo?.trim() || `BPAY-${Date.now()}`,
    payment_date: options?.paymentDate,
    allocation_strategy: 'manual',
    allocations,
    notes: options?.notes,
    created_by: getCurrentUserId(),
  };

  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.BULK_PAYMENT}/${customerId}/payments/bulk`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Bulk payment failed:', response.status, errorText);

    let errorMessage = 'Failed to record bulk payment';
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorMessage;
      if (errorData.errors && typeof errorData.errors === 'object') {
        const parts = Object.entries(errorData.errors).flatMap(([field, msgs]) =>
          (Array.isArray(msgs) ? msgs : [msgs]).map((m: string) => `${field}: ${m}`)
        );
        if (parts.length) errorMessage = parts.join('. ');
      }
    } catch {
      errorMessage = errorText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const result: BulkCustomerPaymentResponse = await response.json();
  return result.data;
};

export const createCustomer = async (customerData: CreateCustomerData): Promise<Customer> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.CREATE}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(customerData),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create customer');
  }

  const result: CreateCustomerResponse = await response.json();
  return result.data;
};

export const updateCustomer = async (
  customerId: number,
  customerData: UpdateCustomerData
): Promise<Customer> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.UPDATE}/${customerId}`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(customerData),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update customer');
  }

  const result: UpdateCustomerResponse = await response.json();
  return result.data;
};

export const mergeCustomers = async (
  duplicateId: number,
  intoCustomerId: number
): Promise<Customer> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.MERGE}/${duplicateId}/merge`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ into_customer_id: intoCustomerId }),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to merge customers');
  }

  const result: MergeCustomersResponse = await response.json();
  return result.data;
};

export const deleteCustomer = async (customerId: number): Promise<void> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.DELETE}/${customerId}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete customer');
  }
}; 